import type { TaskAnalyticsContext } from 'types/analytics';
import type { ProjectBase } from 'types/project';
import type { TicMeta } from 'types/tic';
import type { Duration } from 'types/units';
import type { LightUser } from 'types/user';

export interface ForecastAssignee
  extends Pick<LightUser, 'id' | 'name' | 'avatar' | 'email' | 'updated_at'> {
  weekly_capacity?: number;
  work_days?: string;
}

export interface Forecast {
  id: number;
  temporaryId?: string;
  title: string;
  description: string | null | undefined;
  from: string;
  to: string;
  /** What is the total estimated effort of this task, in minutes? */
  estimated_minutes: number;
  /** How much time has been planned for this task? */
  planned_duration: Duration;
  /** How many minutes have been worked on this forecast? */
  logged_duration: Duration;
  project: ProjectBase | null;
  users: Array<ForecastAssignee>;
  created_at: string;
  updated_at: string | null;
  completed_at: string | null;
  label_ids: Array<number>;
  manage: boolean;
  parent_title: string;

  /**
   * TIC (Timely Integration Cloud) metadata for this task.
   * Use this when displaying task icons, NOT project.tic.
   * @see TicMeta
   */
  tic?: TicMeta;
}

export interface ForecastDraft extends Forecast {
  userLacksProjectPermission?: boolean;
  openFor?: 'editing';
  context: TaskAnalyticsContext;
}

export interface ForecastAllocation {
  /** How many daily minutes is required to get this done in time? */
  dailyWorkMinutesRequiredToCompleteWithinRange: number;
  /** Daily work as percentage of daily capacity */
  dailyUtilizationRate: number;
  /** The user's current daily capacity, null if it has changed during the forecast period */
  currentDailyUserCapacity: number | null;
  /** How many work days remains? */
  minutesOfWorkRemainingAsDays: number;
  /** The user's total capacity for the forecast period **/
  totalCapacityForRange: number | null;
}

export interface EnrichedForecastMeta {
  /** Estimated vs logged, in percent */
  progressedPercent: number;
  /** Estimated vs planned, in percent */
  plannedPercent: number;
  /** Estimated + planned, in percent */
  totalEstimatedProgress: number;
  /** Due in this many days */
  dueInDays: number;
  /** Are we done? */
  isDone: boolean;
  /** Is this task overdue? */
  isOverdue: boolean;
  /** How many days is this delayed, if not null */
  delayedForDays: number | null;
  overtime: number | null;
}
export interface EnrichedForecast extends Forecast {
  meta: EnrichedForecastMeta;
  allocationByUser: Record<number, ForecastAllocation>;
}

export interface EnrichedForecastDraft extends ForecastDraft, EnrichedForecast {}

export const FORECAST_VIEW_TYPES = ['timeline', 'list'] as const;
export type ForecastViewType = (typeof FORECAST_VIEW_TYPES)[number];
